import type { DetalheDte, DteResposta } from './canvas';

import { useRef, useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useParams, useNavigate } from 'react-router';

import Box from '@mui/material/Box';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { DsButton, DsPageHeader, DsStatusChip } from 'src/auditoria/components/ds';

import { SX_COLUNAS, RailCiencia, CardMensagem, GradeNotificacoes } from './canvas';

// ----------------------------------------------------------------------
// DTE → detalhe da notificação (artboard
// `portal-dte-detalhe-protocolo-2026-004807`). É a MESMA tela da lista: o
// canvas mantém a grade ao lado, com a linha aberta destacada, e acrescenta o
// card da mensagem abaixo dela. Por isso esta rota carrega lista e detalhe.
//
// ABRIR a mensagem registra a ciência (pedido de 20/jul: sem botão —
// Notificação/Intimação emitem o Protocolo de Recebimento no ato da abertura;
// Aviso só marca leitura).
// ----------------------------------------------------------------------

export default function DteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();

  const { data, isLoading } = useSWR<DetalheDte>(`/portal-api/dte/${id}`, fetcher);
  const { data: lista, isLoading: carregandoLista } = useSWR<DteResposta>(
    '/portal-api/dte',
    fetcher
  );
  const mensagens = lista?.mensagens ?? [];

  // Abrir = ciência: o BACKEND registra leitura + Protocolo de Recebimento no
  // próprio GET do detalhe — a resposta já vem com `protocolo` preenchido na
  // primeira aberta, sem POST nem reload. Aqui só revalidamos a listagem (badge
  // de não-lidas) uma vez, quando o detalhe chega.
  const listaAtualizada = useRef(false);
  useEffect(() => {
    if (data && !listaAtualizada.current) {
      listaAtualizada.current = true;
      mutate('/portal-api/dte');
    }
  }, [data, mutate]);

  const abrirProtocolo = (assuntoId?: number) => {
    const alvo = assuntoId
      ? `/protocolo/novo?assunto=${assuntoId}${data ? `&dte=${data.id}` : ''}`
      : '/protocolo/novo';
    navigate(alvo);
  };

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Notificações"
        subtitle="Caixa de entrada do Domicílio Tributário Eletrônico · abrir registra ciência e emite Protocolo de Recebimento."
        action={
          <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <DsStatusChip tone={(lista?.naoLidas ?? 0) > 0 ? 'warning' : 'default'}>
              {lista?.naoLidas ?? 0} não lidas
            </DsStatusChip>
            <DsButton
              tone="neutral"
              size="tall"
              icon="atualizar"
              onClick={() => mutate('/portal-api/dte')}
            >
              Atualizar
            </DsButton>
          </Box>
        }
      />

      <Box sx={SX_COLUNAS}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GradeNotificacoes
            mensagens={mensagens}
            carregando={carregandoLista}
            abertaId={data?.id}
            aoAbrir={(row) => navigate(`/notificacoes/${row.id}`)}
          />

          {data && !isLoading && (
            <CardMensagem
              detalhe={data}
              aoContestar={(assuntoId) => abrirProtocolo(assuntoId)}
              aoVoltar={() => navigate('/notificacoes')}
            />
          )}
        </Box>

        <RailCiencia
          mensagens={mensagens}
          naoLidas={lista?.naoLidas ?? 0}
          aoAbrirProtocolo={() => abrirProtocolo()}
        />
      </Box>
    </DashboardContent>
  );
}
